"use client";

import type { GlyphName } from "@/components/landing/shared";
import { useEffect, useState } from "react";
import { Glyph } from "@/components/landing/shared";
import { cn } from "@/lib/utils";

// A looping illustration of one order moving through the delivery flow —
// the rider glides from node to node, then the whole cycle restarts as if
// tracking the next order.
const STEP_DURATION = 2400;
const STEP_ICONS: GlyphName[] = ["check", "person", "motorcycle", "check"];

interface DeliveryTimelineProps {
	steps: string[];
}

function DeliveryTimeline(prop: DeliveryTimelineProps) {
	const { steps } = prop;
	const stepCount = steps.length;
	const [activeStep, setActiveStep] = useState(0);
	const [skipTransition, setSkipTransition] = useState(false);

	useEffect(() => {
		const id = setInterval(() => {
			setActiveStep((prev) => {
				const next = (prev + 1) % stepCount;
				if (next === 0) {
					setSkipTransition(true);
					requestAnimationFrame(() => requestAnimationFrame(() => setSkipTransition(false)));
				}
				return next;
			});
		}, STEP_DURATION);
		return () => clearInterval(id);
	}, [stepCount]);

	const half = 50 / stepCount;
	const trackWidth = 100 - half * 2;
	const fillFraction = activeStep / (stepCount - 1);
	const tipPercent = half + fillFraction * trackWidth;
	const moveTransition = skipTransition
		? "none"
		: "inset-inline-start 0.9s cubic-bezier(0.22, 1, 0.36, 1)";

	return (
		<div className="relative mt-10">
			<div
				className="absolute top-5 h-0.5 bg-border"
				style={{ insetInlineStart: `${half}%`, insetInlineEnd: `${half}%` }}
			/>
			<div
				className="absolute top-5 h-0.5 origin-left rtl:origin-right"
				style={{
					insetInlineStart: `${half}%`,
					width: `${trackWidth}%`,
					background: "var(--brand-1)",
					transform: `scaleX(${fillFraction})`,
					transition: skipTransition ? "none" : "transform 0.9s cubic-bezier(0.22, 1, 0.36, 1)",
				}}
			/>
			<span
				className="absolute top-5 z-10 grid h-6 w-6 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full rtl:translate-x-1/2"
				style={{
					insetInlineStart: `${tipPercent}%`,
					background: "var(--brand-1)",
					color: "#fff",
					boxShadow: "0 4px 10px color-mix(in srgb, var(--brand-1) 45%, transparent)",
					transition: skipTransition ? "none" : `${moveTransition}, opacity 0.3s`,
					opacity: activeStep === 0 ? 0 : 1,
				}}
			>
				<Glyph name="motorcycle" className="h-3.5 w-3.5" />
			</span>
			<div className="relative grid grid-cols-4 gap-2">
				{steps.map((step, i) => {
					const done = i <= activeStep;
					const current = i === activeStep;
					return (
						<div key={step} className="flex flex-col items-center gap-2 text-center">
							<span
								className={cn(
									"grid h-10 w-10 place-items-center rounded-full transition-colors duration-500",
									current ? "delivery-step-active" : "",
								)}
								style={
									done
										? { background: "var(--brand-1)", color: "#fff" }
										: {
												border: "1px solid var(--border)",
												background: "var(--surface)",
												color: "var(--text-muted)",
											}
								}
							>
								<Glyph name={STEP_ICONS[i]} className="h-4 w-4" />
							</span>
							<span
								className={cn(
									"text-xs transition-colors duration-500 sm:text-sm",
									done ? "font-semibold" : "text-muted-foreground",
								)}
							>
								{step}
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);
}

export default DeliveryTimeline;
